import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { Theme } from '@mui/material/styles';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import DurationDropdown from 'common/components/DurationDropdown';
import {
	getDurationOptionsForStockProducts,
	getDurationOptionsForOrderProducts,
	getSelectedDurationOptionIndex,
} from 'common/utils/pricing';
import { Duration, DurationWithPrice, LocaleField, OpeningHoursWithExceptions } from 'common/types';
import { getDurationTypeFromTimePeriod } from 'common/utils/pricing';
import { getDurationString } from 'common/utils/dateCalculations';

import * as ViewActions from 'actions/ViewActions';
import * as NewRentalActions from 'actions/NewRentalActions';

import * as ViewSelectors from 'selectors/ViewSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NewRentalSelectors from 'selectors/NewRentalSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import {
	getOpeningHoursBasedOnLocation,
	getExceptionPeriodsBasedOnLocation,
} from 'common/utils/shopUtils';

import { useTranslation } from 'services/localization/useTranslation';
import { isProductVisibleInLocation } from 'common/modules/products/utils';
const DurationSelect = () => {
	const classes = useStyles();
	const dispatch = useDispatch();

	const { t } = useTranslation();

	const startDate = useSelector(ViewSelectors.newRentalStartDate);
	const startLocation = useSelector(NewRentalSelectors.startLocation);
	const language = useSelector(ViewSelectors.lang);
	const rentalProducts = useSelector(NewRentalSelectors.products);
	const endLocation = useSelector(NewRentalSelectors.endLocation);
	const shopMainLocationId = useSelector(ShopSelectors.activeShopMainLocationId);
	const stockProductsLoading = useSelector(StockSelectors.stockProductsLoading);
	const stockProducts = useSelector(StockSelectors.stockProductsData);
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);
	const shopDateFormat = useSelector(ShopSelectors.getShopDateFormat);
	const shopOpeningHours = useSelector(ShopSelectors.activeLocationOpeningHoursWithException);
	const shopPricingTables = useSelector(ShopSelectors.activeShopPricingTables);
	const duration = useSelector(ViewSelectors.newRentalDuration);
	const isDurationManuallyChanged = useSelector(NewRentalSelectors.isDurationManuallyChanged);

	const endLocationOpeningHours: OpeningHoursWithExceptions = useMemo(
		() => ({
			openingHours: getOpeningHoursBasedOnLocation(shopOpeningHours.openingHours, endLocation.id),
			exceptionPeriods: getExceptionPeriodsBasedOnLocation(
				shopOpeningHours.exceptionPeriods,
				endLocation.id,
				shopMainLocationId,
				shopOpeningHours.openingHours,
			),
		}),
		[endLocation.id, shopMainLocationId, shopOpeningHours],
	);

	const options = useMemo(() => {
		if (!rentalProducts.length) {
			const activeLocationStockProducts = stockProducts.filter((p) =>
				isProductVisibleInLocation(p, startLocation.id),
			);
			return getDurationOptionsForStockProducts(
				activeLocationStockProducts,
				shopPricingTables,
				endLocationOpeningHours,
				startDate,
				'ADMIN',
			);
		}

		return getDurationOptionsForOrderProducts(
			rentalProducts,
			stockProducts,
			shopPricingTables,
			endLocationOpeningHours,
			startDate,
			'ADMIN',
		);
	}, [
		endLocationOpeningHours,
		rentalProducts,
		shopPricingTables,
		startDate,
		startLocation.id,
		stockProducts,
	]);

	const selectedDurationIndex = getSelectedDurationOptionIndex(options, duration);
	const isCustomDuration = selectedDurationIndex === -1;

	const handleDurationChange = useCallback(
		(durationOption: DurationWithPrice | null, userTriggered: boolean = true) => {
			if (!durationOption) {
				return;
			}

			if (userTriggered) {
				dispatch(NewRentalActions.setIsDurationManuallyChanged(true));
			}

			const { durationInSeconds, timePeriod, label, endTime } = durationOption;
			const durationType = getDurationTypeFromTimePeriod(timePeriod);
			const durationName = label || null;
			const newEndDate =
				endTime ?? moment(startDate).add(durationInSeconds, 'seconds').toISOString();
			const duration: Duration = {
				durationInSeconds,
				durationType,
				durationName,
			};
			dispatch(ViewActions.setEndDate(newEndDate));
			dispatch(ViewActions.setDuration(duration));
			dispatch(NewRentalActions.setOrderProductsDates(startDate, newEndDate, duration));
		},
		[dispatch, startDate],
	);

	useEffect(() => {
		if (!stockProductsLoading && !!options.length && !isDurationManuallyChanged) {
			const selectedDuration = options[selectedDurationIndex];
			if (
				!selectedDuration ||
				selectedDuration.durationInSeconds !== options[0].durationInSeconds
			) {
				handleDurationChange(options[0], false);
			}
		}
	}, [
		stockProductsLoading,
		options,
		handleDurationChange,
		selectedDurationIndex,
		isDurationManuallyChanged,
	]);

	const optionsWithCustom = useMemo(() => {
		const localeLabel: LocaleField = {
			def: getDurationString(duration, 'long', language, t),
		};
		const customDurationOption: DurationWithPrice | null = isCustomDuration
			? {
					disabled: false,
					timePeriod:
						duration.durationType === 'opening_hours' ? 'days_within_opening_hours' : 'hours',
					timeValue: duration.durationInSeconds / 60 / 60,
					price: 0,
					durationInSeconds: duration.durationInSeconds,
					label: localeLabel,
			  }
			: null;

		return customDurationOption ? [customDurationOption, ...options] : options;
	}, [options, language, isCustomDuration, t, duration]);

	return (
		<DurationDropdown
			value={selectedDurationIndex === -1 ? 0 : selectedDurationIndex}
			onValueChange={handleDurationChange}
			options={optionsWithCustom}
			text={{
				label: t('newRental.rentalTime'),
				shopClosing: t('newRental.shopClosing'),
			}}
			InputProps={{
				classes: {
					root: classes.filledInput,
				},
			}}
			lang={language}
			t={t}
			variant="filled"
			noPrice={true}
			currency={shopCurrency}
			shopDateFormat={shopDateFormat}
			margin="none"
		/>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		filledInput: {
			backgroundColor: `${theme.palette.background.paper} !important`,
			borderRadius: 0,
		},
	}),
);

export default DurationSelect;
